import type { PropsWithChildren, ReactElement } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import {
  OrganizationRoleType,
  PlatformFeatureType,
} from "@/types/graphql/partner";
import {
  hasPlatformFeature,
  useCurrentPlatformQuery,
} from "@/partner/entities/platforms";
import { useLiveEnabledState } from "@/partner/entities/instance";
import { PagePaths } from "@/partner/shared/constants";
import { RequireAbility } from "@/partner/shared/ui";
import { Header, Layout, Sidebar } from "@/partner/widgets";
import {
  DashboardPage,
  NotFoundPage,
  UserSettingsPage,
  PlatformSettingsPage,
  PlatformBrandingPage,
  PlatformSocialPage,
  PlatformCustomCoversPage,
  PlatformPagesPage,
  PlatformMenuPage,
  TagsPage,
  CountryAccessesPage,
  PostsPage,
  VideosPage,
  StoragePage,
  CollectionsPage,
  ProductsPage,
  PromocodesPage,
  StreamsPage,
  StreamEntrypointsPage,
  ChatPage,
  LinksPage,
  WidgetsPage,
  PollsPage,
  BroadcastsPage,
  AutomationsPage,
  EmailTemplatesPage,
  MarketingContactsPage,
  UsersPage,
  AudienceConversationsPage,
  AudienceRequestsPage,
  PurchasesPage,
  PaymentsPage,
  BillingPage,
  ChargebacksPage,
  SubscriptionsPage,
  AnalyticsPage,
  TeamPage,
  ReportsPage,
} from "@/partner/pages";

// Видео и трансляции делят субъект Content (см. shared/constants/menu.ts),
// поэтому маршруты гейтятся ролями напрямую, как и пункты меню.
const VIDEO_ROLES = [
  OrganizationRoleType.VideosType,
  OrganizationRoleType.AdminType,
  OrganizationRoleType.AnalyticsType,
];
const STREAM_ROLES = [
  OrganizationRoleType.StreamsType,
  OrganizationRoleType.AdminType,
  OrganizationRoleType.AnalyticsType,
];
// read User раздаётся всем непустым ролям (нужно для подписей — см.
// task1-report.md), поэтому раздел зрителей гейтится ролями напрямую, а
// не ability, как videos/streams/collections.
// Модерация чата общая для видео и эфиров: страница доступна обеим
// контентным ролям, аналитик её не видит (действия на ней пишущие).
const CHAT_ROLES = [
  OrganizationRoleType.VideosType,
  OrganizationRoleType.StreamsType,
  OrganizationRoleType.AdminType,
];
const USERS_ROLES = [
  OrganizationRoleType.UsersType,
  OrganizationRoleType.AdminType,
];

/**
 * Гейт страниц живых эфиров (`instance.liveEnabled`). На развёртывании без
 * эфиров это не «недостаточно прав», а «такого раздела здесь нет», поэтому
 * вместо AccessDeniedScreen редирект на дашборд: пункта меню тоже нет
 * (shared/constants/menu.ts, requiresLive), и попасть сюда можно только по
 * сохранённой ссылке.
 *
 * Пока ответ о возможностях инстанса не пришёл, не рендерится ничего:
 * страница эфиров успела бы отправить свои запросы, а редирект увёл бы
 * партнёра ottkit.io с валидного адреса.
 */
const RequireLive = ({ children }: PropsWithChildren): ReactElement | null => {
  const { liveEnabled, loading } = useLiveEnabledState();

  if (loading) return null;
  if (!liveEnabled) return <Navigate to={PagePaths.partner} replace />;

  return <>{children}</>;
};

/**
 * Гейт разделов за фичефлагом платформы («Опросы», «Письма»):
 * без флага раздела нет (пункта меню тоже), поэтому редирект на дашборд, а
 * не экран «Недостаточно прав». Пока платформа не загрузилась, не рендерим
 * ничего.
 */
const RequirePlatformFeature = ({
  feature,
  children,
}: PropsWithChildren<{
  feature: PlatformFeatureType;
}>): ReactElement | null => {
  const { data, loading } = useCurrentPlatformQuery();

  if (loading && !data) return null;
  if (!hasPlatformFeature(data?.currentPlatform?.features, feature)) {
    return <Navigate to={PagePaths.partner} replace />;
  }

  return <>{children}</>;
};

/** Адреса удалённых страниц, на которые могли остаться ссылки. */
const LegacyPaths = {
  liveReactions: "/partner/live-reactions",
  marketing: "/partner/marketing",
  marketingSettings: "/partner/marketing/settings",
  marketingEmail: "/partner/marketing/emails/:id",
} as const;

/**
 * Бывшая страница редактора письма: «new» открывает диалог создания,
 * id открывает диалог редактирования на странице списка писем.
 */
const LegacyEmailTemplateRedirect = (): ReactElement => {
  const { id } = useParams<{ id: string }>();
  const search =
    !id || id === "new"
      ? "?tab=create"
      : `?${new URLSearchParams({ tab: "edit", id }).toString()}`;

  return <Navigate to={`${PagePaths.emailTemplates}${search}`} replace />;
};

export const Router = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Standalone-domain deploy (partner.ottkit.io): the bare domain
            root is "/", not PagePaths.partner ("/partner") — that prefix
            only applies to the combined admin+partner build (local dev).
            Without this, visiting the domain root hits the catch-all "*"
            below and renders NotFoundPage instead of the dashboard. */}
        <Route path="/" element={<Navigate to={PagePaths.partner} replace />} />
        <Route element={<Layout header={Header} sidebar={Sidebar} />}>
          {/* Дашборд не оборачивается RequireAbility: недостаток прав на
              него обрабатывает Layout — редирект на первый доступный
              пункт меню, а не AccessDeniedScreen на месте (спека, «Первая
              доступная страница после входа»). */}
          <Route index path={PagePaths.partner} element={<DashboardPage />} />
          <Route path={PagePaths.userSettings} element={<UserSettingsPage />} />
          <Route
            path={PagePaths.platformSettings}
            element={
              <RequireAbility action="update" subject="Platform">
                <PlatformSettingsPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.platformBranding}
            element={
              <RequireAbility action="update" subject="Platform">
                <PlatformBrandingPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.platformSocial}
            element={
              <RequireAbility action="update" subject="Platform">
                <PlatformSocialPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.platformCustomCovers}
            element={
              <RequireAbility action="read" subject="PlatformCustomCover">
                <PlatformCustomCoversPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.platformPages}
            element={
              <RequireAbility action="read" subject="PlatformPage">
                <PlatformPagesPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.platformMenu}
            element={
              <RequireAbility action="read" subject="PlatformMenuItem">
                <PlatformMenuPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.tags}
            element={
              <RequireAbility action="read" subject="Tag">
                <TagsPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.countryAccesses}
            element={
              <RequireAbility action="read" subject="CountryAccess">
                <CountryAccessesPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.posts}
            element={
              <RequireAbility action="read" subject="Post">
                <PostsPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.videos}
            element={
              <RequireAbility roles={VIDEO_ROLES}>
                <VideosPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.storage}
            element={
              <RequireAbility action="read" subject="Storage">
                <StoragePage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.collections}
            element={
              <RequireAbility roles={VIDEO_ROLES}>
                <CollectionsPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.products}
            element={
              <RequireAbility action="read" subject="Product">
                <ProductsPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.promocodes}
            element={
              <RequireAbility action="read" subject="Promocode">
                <PromocodesPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.streams}
            element={
              <RequireLive>
                <RequireAbility roles={STREAM_ROLES}>
                  <StreamsPage />
                </RequireAbility>
              </RequireLive>
            }
          />
          <Route
            path={PagePaths.chat}
            element={
              <RequireAbility roles={CHAT_ROLES}>
                <ChatPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.streamEntrypoints}
            element={
              <RequireLive>
                <RequireAbility action="read" subject="StreamEntrypoint">
                  <StreamEntrypointsPage />
                </RequireAbility>
              </RequireLive>
            }
          />
          <Route
            path={PagePaths.links}
            element={
              <RequireAbility action="read" subject="Link">
                <LinksPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.widgets}
            element={
              <RequireAbility action="read" subject="Widget">
                <WidgetsPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.polls}
            element={
              <RequirePlatformFeature
                feature={PlatformFeatureType.PlayerPollsType}
              >
                <RequireAbility action="read" subject="Poll">
                  <PollsPage />
                </RequireAbility>
              </RequirePlatformFeature>
            }
          />
          <Route
            path={PagePaths.broadcasts}
            element={
              <RequirePlatformFeature
                feature={PlatformFeatureType.MarketingEmailsType}
              >
                <RequireAbility action="read" subject="Automation">
                  <BroadcastsPage />
                </RequireAbility>
              </RequirePlatformFeature>
            }
          />
          <Route
            path={PagePaths.automations}
            element={
              <RequirePlatformFeature
                feature={PlatformFeatureType.MarketingEmailsType}
              >
                <RequireAbility action="read" subject="Automation">
                  <AutomationsPage />
                </RequireAbility>
              </RequirePlatformFeature>
            }
          />
          <Route
            path={PagePaths.emailTemplates}
            element={
              <RequirePlatformFeature
                feature={PlatformFeatureType.MarketingEmailsType}
              >
                <RequireAbility action="read" subject="EmailTemplate">
                  <EmailTemplatesPage />
                </RequireAbility>
              </RequirePlatformFeature>
            }
          />
          <Route
            path={PagePaths.marketingContacts}
            element={
              <RequirePlatformFeature
                feature={PlatformFeatureType.MarketingEmailsType}
              >
                <RequireAbility action="read" subject="ContactList">
                  <MarketingContactsPage />
                </RequireAbility>
              </RequirePlatformFeature>
            }
          />
          {/* Старые адреса: реакции и настройки рассылок стали блоками
              настроек платформы, редактор письма стал диалогом списка. */}
          <Route
            path={LegacyPaths.liveReactions}
            element={<Navigate to={PagePaths.platformSettings} replace />}
          />
          <Route
            path={LegacyPaths.marketingSettings}
            element={<Navigate to={PagePaths.platformSettings} replace />}
          />
          <Route
            path={LegacyPaths.marketing}
            element={<Navigate to={PagePaths.emailTemplates} replace />}
          />
          <Route
            path={LegacyPaths.marketingEmail}
            element={<LegacyEmailTemplateRedirect />}
          />
          <Route
            path={PagePaths.users}
            element={
              <RequireAbility roles={USERS_ROLES}>
                <UsersPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.audienceConversations}
            element={
              <RequireAbility action="read" subject="AssistantConversation">
                <AudienceConversationsPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.audienceRequests}
            element={
              <RequireAbility action="read" subject="SupportRequest">
                <AudienceRequestsPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.purchases}
            element={
              <RequireAbility action="read" subject="ProductPurchase">
                <PurchasesPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.payments}
            element={
              <RequireAbility action="read" subject="Payment">
                <PaymentsPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.billing}
            element={
              <RequireAbility action="read" subject="ContractPayment">
                <BillingPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.chargebacks}
            element={
              <RequireAbility action="read" subject="Refund">
                <ChargebacksPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.subscriptions}
            element={
              <RequireAbility action="read" subject="Subscription">
                <SubscriptionsPage />
              </RequireAbility>
            }
          />
          <Route
            path={`${PagePaths.analytics}/*`}
            element={
              <RequireAbility action="read" subject="Analytics">
                <AnalyticsPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.team}
            element={
              <RequireAbility action="manage" subject="OrganizationUser">
                <TeamPage />
              </RequireAbility>
            }
          />
          <Route
            path={PagePaths.reports}
            element={
              <RequireAbility action="read" subject="Report">
                <ReportsPage />
              </RequireAbility>
            }
          />
          <Route path={"*"} element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};
